import { useState, useEffect, useRef } from "react";
import { MessageCircle, Mail, HelpCircle, X, Phone } from "lucide-react";

const WHATSAPP_NUMBER = "393291848135"; // +39 329 184 8135 (no spaces)
const WHATSAPP_PREFILL = encodeURIComponent("Ciao Nautiqo, ho bisogno di aiuto.");
const WHATSAPP_URL = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${WHATSAPP_PREFILL}`;
const SUPPORT_EMAIL = "info@nautiqo.cloud";
const FAQ_URL = "/landing#faq";

// Open external URL safely from inside iframes (e.g. Emergent preview).
// Falls back to top-level navigation if window.open is blocked.
const openExternal = (url) => {
  try {
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w || w.closed) {
      // Popup blocked — try parent window then fallback to top redirect
      try { window.top.location.href = url; }
      catch { window.location.href = url; }
    }
  } catch {
    window.location.href = url;
  }
};

/**
 * StickyHelpButton — floating bottom-right help launcher.
 * Always-visible inside /admin/* (mounted by AdminLayout).
 */
export const StickyHelpButton = () => {
  const [open, setOpen] = useState(false);
  const [bounced, setBounced] = useState(false);
  const ref = useRef(null);

  // Initial bounce after 1.5s on first mount
  useEffect(() => {
    const t = setTimeout(() => setBounced(true), 1400);
    return () => clearTimeout(t);
  }, []);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="fixed bottom-6 right-6 z-[60]" data-testid="help-launcher">
      {/* Expanded menu */}
      <div
        className={`absolute bottom-16 right-0 w-72 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 origin-bottom-right ${
          open ? "opacity-100 scale-100 translate-y-0 pointer-events-auto" : "opacity-0 scale-95 translate-y-2 pointer-events-none"
        }`}
        data-testid="help-menu"
      >
        <div className="px-5 py-4 border-b border-white/5">
          <p className="text-sm font-semibold text-white">Hai bisogno di aiuto?</p>
          <p className="text-xs text-slate-400 mt-0.5">Rispondiamo in pochi minuti</p>
        </div>
        <div className="p-2">
          <a
            href={WHATSAPP_URL}
            onClick={(e) => { e.preventDefault(); openExternal(WHATSAPP_URL); setOpen(false); }}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition group"
            data-testid="help-whatsapp"
          >
            <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center group-hover:bg-emerald-500/25 transition">
              <MessageCircle className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-white">WhatsApp</p>
              <p className="text-[11px] text-slate-500">+39 329 184 8135</p>
            </div>
          </a>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=Richiesta%20supporto%20Nautiqo`}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition group"
            data-testid="help-email"
          >
            <div className="w-9 h-9 rounded-full bg-blue-500/15 flex items-center justify-center group-hover:bg-blue-500/25 transition">
              <Mail className="w-4 h-4 text-blue-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-white">Email</p>
              <p className="text-[11px] text-slate-500">{SUPPORT_EMAIL}</p>
            </div>
          </a>
          <a
            href={FAQ_URL}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition group"
            data-testid="help-faq"
          >
            <div className="w-9 h-9 rounded-full bg-violet-500/15 flex items-center justify-center group-hover:bg-violet-500/25 transition">
              <HelpCircle className="w-4 h-4 text-violet-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-white">FAQ</p>
              <p className="text-[11px] text-slate-500">Domande frequenti</p>
            </div>
          </a>
        </div>
        <div className="px-5 py-3 border-t border-white/5 flex items-center gap-2">
          <Phone className="w-3 h-3 text-slate-500" />
          <span className="text-[11px] text-slate-500">Lun-Ven 9:00-18:00</span>
        </div>
      </div>

      {/* Trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`relative w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-2xl shadow-blue-900/50 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform ${
          !open && bounced ? "animate-help-bounce" : ""
        }`}
        aria-label={open ? "Chiudi aiuto" : "Apri aiuto"}
        data-testid="help-button"
      >
        {/* Pulsing dot */}
        {!open && (
          <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-slate-900">
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
          </span>
        )}
        <div className="relative">
          {open ? <X className="w-5 h-5" /> : <HelpCircle className="w-6 h-6" />}
        </div>
      </button>
    </div>
  );
};
